import dateFormat from 'dateformat';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { EdmundsAPI } from 'client/data/api/api-client';
import { withMetrics } from 'client/data/api/api-metrics';
import {
  DEALER_REVIEWS_PER_PAGE,
  REVIEWS_ALL,
  REVIEWS_SALES,
  REVIEWS_SERVICE,
  SORT_RATING,
  SORT_ORDER_DESC,
  SORT_UPDATED,
} from 'client/constants/dealer-reviews';

function onSuccess(json) {
  return json || null;
}

function onReject() {
  return Promise.resolve({});
}

export const ReviewEntity = PropTypes.shape({
  id: PropTypes.string,
  author: PropTypes.string,
  created: PropTypes.string,
  createdIso: PropTypes.number,
  updated: PropTypes.string,
  updatedIso: PropTypes.number,
  text: PropTypes.string,
  title: PropTypes.string,
  rating: PropTypes.number,
  reviewType: PropTypes.oneOf([REVIEWS_SALES, REVIEWS_SERVICE]),
  isDealerRecommended: PropTypes.string,
  wasCarBoughtHere: PropTypes.string,
  wereYouSatisfied: PropTypes.string,
  wasPriceAppropriate: PropTypes.string,
  didDealerCompleteWork: PropTypes.string,
  didDealerAllCommitmentsMade: PropTypes.string,
  didDealerProvideQuote: PropTypes.string,
  hasPreviousDealer: PropTypes.bool,
});

export const ReviewEntities = {
  ReviewEntity,
  ReviewsByType: PropTypes.shape({
    totalCount: PropTypes.number,
    newReviews: PropTypes.arrayOf(ReviewEntity),
    oldReviews: PropTypes.arrayOf(ReviewEntity),
  }),
  OverallRatingEntity: PropTypes.shape({
    averageRating: PropTypes.number,
    recommended: PropTypes.number,
    notRecommended: PropTypes.number,
    totalCount: PropTypes.number,
  }),
  TopReviewsEntity: PropTypes.shape({
    sales: ReviewEntity,
    service: ReviewEntity,
  }),
  CommentsEntity: PropTypes.shape({
    authorName: PropTypes.string,
    created: PropTypes.string,
    text: PropTypes.string,
    dealerComment: PropTypes.bool,
    id: PropTypes.string,
  }),
  ReviewsFilter: PropTypes.shape({
    type: PropTypes.string,
    page: PropTypes.string.isRequired,
    sortBy: PropTypes.string.isRequired,
    order: PropTypes.string.isRequired,
  }),
};

export const DealerReviewEntity = PropTypes.shape({
  id: PropTypes.string,
  dealerId: PropTypes.string,
  dealerName: PropTypes.string,
  title: PropTypes.string,
  text: PropTypes.string,
  reviewType: PropTypes.string,
  stateCode: PropTypes.string,
  created: PropTypes.number,
  updated: PropTypes.number,
  author: PropTypes.shape({
    authorName: PropTypes.string,
  }),
  ratings: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      value: PropTypes.number,
    })
  ),
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      authorName: PropTypes.string,
      created: PropTypes.number,
      updated: PropTypes.number,
      text: PropTypes.string,
      dealerComment: PropTypes.bool,
    })
  ),
  questionnaire: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string,
      answer: PropTypes.string,
    })
  ),
  hasPreviousDealer: PropTypes.bool,
});

/**
 * If the questions was answered by the user, then returns a formated answer, otherwise returns null.
 * @return {'Yes'|'No'|'Not Applicable'|null}
 */
function getUserAnswer(questionnaire, questionText) {
  const question = questionnaire.find(item => item.question === questionText);
  const answer = question || (question && question.answer === null) ? question.answer.toLowerCase() : null;

  if (answer === 'true' || answer === 'yes') {
    return 'Yes';
  } else if (answer === 'false' || answer === 'no') {
    return 'No';
  } else if (answer === 'not_applicable') {
    return 'Not Applicable';
  }
  return null;
}

function getReviewRating(ratings) {
  const rating = ratings.find(item => item.type === 'OVERALL_RATING');
  return rating && rating.value;
}
export function formatCommentsDate(comments) {
  return comments.reduce((accumulator, item) => {
    // per PROD-401048, comments on review should be displayed immediately after they are submitted
    if (item.moderationStatus === 'STATUS_PASSED' || item.moderationStatus === 'STATUS_NOT_MODERATED') {
      accumulator.push({
        authorName: item.authorName,
        text: item.text,
        updated: item.updated,
        dealerComment: item.dealerComment,
        created: dateFormat(item.created, 'mm/dd/yyyy'),
        id: `${item.created}${item.authorName}`,
      });
    }
    return accumulator;
  }, []);
}

export function formatReviewType(reviewType) {
  switch (reviewType) {
    case 'SALES_TYPE':
      return REVIEWS_SALES;
    case 'SERVICE_TYPE':
      return REVIEWS_SERVICE;
    default:
      return 'UNKNOWN';
  }
}

export function getReview(review) {
  if (!review) {
    return null;
  }

  return {
    id: review.id,
    author: review.author.authorName,
    created: dateFormat(review.created, 'mm/dd/yyyy'),
    createdIso: review.created,
    updated: dateFormat(review.updated, 'mm/dd/yyyy'),
    updatedIso: review.updated,
    text: review.text,
    title: review.title,
    reviewType: formatReviewType(review.reviewType),
    comments: formatCommentsDate(review.comments),
    rating: getReviewRating(review.ratings),
    isDealerRecommended: getUserAnswer(review.questionnaire, 'DEALER_RECOMMENDED'),
    wasCarBoughtHere: getUserAnswer(review.questionnaire, 'CAR_BOUGHT'),
    wereYouSatisfied: getUserAnswer(review.questionnaire, 'WERE_YOU_SATISFIED_WITH_WORK_QUALITY'),
    wasPriceAppropriate: getUserAnswer(review.questionnaire, 'WAS_PRICE_APPROPRIATE'),
    didDealerCompleteWork: getUserAnswer(review.questionnaire, 'DID_DEALER_COMPLETE_WORK_WITHIN_PROMISED_TIME'),
    didDealerAllCommitmentsMade: getUserAnswer(review.questionnaire, 'DID_DEALER_HONOR_ALL_COMMITMENTS_MADE'),
    didDealerProvideQuote: getUserAnswer(review.questionnaire, 'DID_DEALER_PROVIDE_YOU_WITH_QUOTE_OF_WORK'),
    hasPreviousDealer: !isEmpty(get(review, 'previousDealer', false)),
  };
}

// unfortunately we have to re-sort when sorted by rating because of the two separate lists of reviews
// that are returned by the api. ideally we would just get back one list of reviews so we wouldn't have
// to do this.

export function sortAllReviewsByRating(reviews, order) {
  return reviews.sort((reviewA, reviewB) => {
    if (reviewA.rating === reviewB.rating) {
      // sort by newest reviews first if the rating is the same
      return reviewB.createdIso - reviewA.createdIso;
    }

    return order === SORT_ORDER_DESC ? reviewB.rating - reviewA.rating : reviewA.rating - reviewB.rating;
  });
}

function prepareReviewsList(reviews, sortBy, order) {
  const DEFAULT_ARRAY = [];
  let newReviews = (reviews.newReviews || DEFAULT_ARRAY).map(getReview);
  let oldReviews = (reviews.oldReviews || DEFAULT_ARRAY).map(getReview);

  if (sortBy === SORT_RATING) {
    newReviews = sortAllReviewsByRating(newReviews, order);
    oldReviews = sortAllReviewsByRating(oldReviews, order);
  }

  return {
    newReviews,
    oldReviews,
  };
}

export function getAllReviewsData({ reviewsData, sortBy, order }) {
  return {
    totalCount: reviewsData.totalCount,
    ...prepareReviewsList(reviewsData, sortBy, order),
  };
}

function formatTopReviewsData(response) {
  return {
    sales: getReview(response.topSalesReview),
    service: getReview(response.topServiceReview),
  };
}

/**
 * https://qa-11-www.edmunds.com/api/dealerreviews/v2/dealers/827?type=sales&sortBy=createdate:DESC&pageNum=1&pageSize=100
 */
function getReviewsByDealerIdAndType({
  dealerId,
  type = 'all',
  page = '1',
  sortBy = SORT_UPDATED,
  order = 'DESC',
  context,
}) {
  // todo add back in type and do the logic correctly in dealer-reviews
  const url = `/dealerreviews/v2/dealers/${dealerId}?type=${type}&sortBy=${sortBy}:${order}&pageNum=${page}&pageSize=${DEALER_REVIEWS_PER_PAGE}`;
  return withMetrics(EdmundsAPI, context)
    .fetchJson(url)
    .then(onSuccess, onReject)
    .then(reviewsData => getAllReviewsData({ reviewsData, sortBy, order }));
}

function getOverallRatingById(dealerId, context) {
  const url = `/dealerreviews/v3/dealers/${dealerId}/ratings/average`;
  return withMetrics(EdmundsAPI, context)
    .fetchJson(url, { retries: 0 })
    .then(onSuccess, onReject);
}

function getTopReviewsByDealerId(dealerId, context) {
  const url = `/dealerreviews/v3/dealers/${dealerId}/topreviews`;
  return withMetrics(EdmundsAPI, context)
    .fetchJson(url, { retries: 0 })
    .then(onSuccess, onReject)
    .then(response => formatTopReviewsData(response));
}

function getDealershipReviewsPath({ dealerId }) {
  return `dealershipReviews.id["${dealerId}"]`;
}

function getTopReviewsPath({ dealerId }) {
  return `${getDealershipReviewsPath({ dealerId })}.topReviews`;
}

function getOverallRatings({ dealerId }) {
  return `${getDealershipReviewsPath({ dealerId })}.overallRating`;
}

export function getDealershipReviewsByTypePath({ type, dealerId }) {
  return `${getDealershipReviewsPath({ dealerId })}.${type}`;
}

function getDealershipReviewPath({ reviewId }) {
  return reviewId ? `dealershipReviews.review.id["${reviewId}"]` : '';
}

export const ReviewsPaths = {
  getTopReviewsPath,
  getOverallRatings,
  getDealershipReviewsByTypePath,
  getReviewsFilterPath() {
    return 'reviewsFilter';
  },
  getDealershipReviewPath,
};

export const ReviewsModel = createModelSegment('dealerReviews', [
  /**
   * @return {ReviewEntities.ReviewsFilter}
   */
  {
    path: 'reviewsFilter',
    resolve() {
      return Promise.resolve({
        type: null,
        page: '1',
        sortBy: SORT_UPDATED,
        order: SORT_ORDER_DESC,
      });
    },
    update(newValue, match, context) {
      return context.resolveValue('reviewsFilter').then(reviewsFilter => ({
        ...reviewsFilter,
        ...newValue,
      }));
    },
  },
  /**
   * @return {ReviewEntities.ReviewsByType}
   */
  {
    path: 'dealershipReviews.id["{dealerId}"].all',
    resolve({ dealerId }, context) {
      return context.resolveValue('reviewsFilter').then(reviewsFilter =>
        getReviewsByDealerIdAndType({
          ...reviewsFilter,
          dealerId,
          context,
          type: REVIEWS_ALL,
        })
      );
    },
  },
  /**
   * @return {ReviewEntities.ReviewsByType}
   */
  {
    path: 'dealershipReviews.id["{dealerId}"].sales',
    resolve({ dealerId }, context) {
      return context.resolveValue('reviewsFilter').then(reviewsFilter =>
        getReviewsByDealerIdAndType({
          ...reviewsFilter,
          dealerId,
          context,
          type: REVIEWS_SALES,
        })
      );
    },
  },
  /**
   * @return {ReviewEntities.ReviewsByType}
   */
  {
    path: 'dealershipReviews.id["{dealerId}"].service',
    resolve({ dealerId }, context) {
      return context.resolveValue('reviewsFilter').then(reviewsFilter =>
        getReviewsByDealerIdAndType({
          ...reviewsFilter,
          dealerId,
          context,
          type: REVIEWS_SERVICE,
        })
      );
    },
  },
  /**
   * @return {ReviewEntities.OverallRatingEntity}
   */
  {
    path: 'dealershipReviews.id["{dealerId}"].overallRating',
    resolve(match, context) {
      return getOverallRatingById(match.dealerId, context);
    },
  },
  /**
   * @return {ReviewEntities.TopReviewsEntity}
   */
  {
    path: 'dealershipReviews.id["{dealerId}"].topReviews',
    resolve(match, context) {
      return getTopReviewsByDealerId(match.dealerId, context);
    },
  },
  /**
   * Get a single review by reviewId. 'no-cache' is used to see new comments on reload
   * @return {ReviewEntities.ReviewEntity}
   * @see getDealershipReviewPath
   */
  {
    path: 'dealershipReviews.review.id["{reviewId}"]',
    resolve(match, context) {
      const url = `/dealerreviews/v2/${match.reviewId}/?view=basic`;
      return withMetrics(EdmundsAPI, context).fetchJson(url, { headers: { 'Cache-Control': 'no-cache' } });
    },
  },
]);
